// ========================================
// pages/Dashboard.js - User Dashboard
// ========================================
// LAST MODIFIED: April 12, 2026
// 
// LAYOUT:
// - Desktop: All action buttons in header row
// - Mobile: Row 1 = Logo + Hamburger, Row 2 = I'm Alive + Avatar (with dropdown)
// - Avatar dropdown contains: My Account, Verification Settings, Logout
// - Logout appears ONLY in avatar dropdown (not as separate button)
// ========================================

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import InheritanceSection from '../components/InheritanceSection';
import Footer from '../components/Footer';

const Dashboard = () => {
  const navigate = useNavigate();
  
  // State variables
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [trusteesCount, setTrusteesCount] = useState(0);
  const [activeTab, setActiveTab] = useState('assets');
  const [downloadingDoc, setDownloadingDoc] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingAsset, setDeletingAsset] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
const getCurrencySymbol = (code) => {
  const symbols = { GHS: '₵', USD: '$', GBP: '£', EUR: '€', NGN: '₦', ZAR: 'R', CAD: 'CA$', AUD: 'A$' };
  if (symbols[code]) return symbols[code];
  // If code is not in symbols (e.g., "JPY", "CHF"), return the code itself
  return code;
};

// ========================================
// FILTER ASSETS BASED ON SEARCH TERM
// ========================================
const filteredAssets = assets.filter(asset => {
  // Check search term
  let matchesSearch = true;
  if (searchTerm.trim()) {
    const term = searchTerm.toLowerCase().trim();
    matchesSearch = 
      asset.assetName?.toLowerCase().includes(term) ||
      asset.assetType?.toLowerCase().replace('_', ' ').includes(term) ||
      asset.institutionName?.toLowerCase().includes(term) ||
      asset.description?.toLowerCase().includes(term);
  }
  
  // Check category filter
  let matchesCategory = true;
  if (selectedCategory !== 'all') {
    matchesCategory = asset.category === selectedCategory;
  }
  
  // Asset must match BOTH search AND category
  return matchesSearch && matchesCategory;
});

  // ========================================
  // DATA FETCHING
  // ========================================
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) setUser(JSON.parse(userStr));
    fetchUserData();
    fetchTrusteesCount();
  }, []);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data.user);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    } catch (err) {
      console.error('Failed to fetch user data:', err);
    }
  };

const fetchTrusteesCount = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/trustees`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    // If response.data is an array, set its length; otherwise set to 0
    setTrusteesCount(Array.isArray(response.data) ? response.data.length : 0);
  } catch (err) {
    // Don't show error to user - just set count to 0
    console.log('No trustees found or error fetching trustees');
    setTrusteesCount(0);
  }
};

  // ========================================
  // CHECK-IN FUNCTION
  // ========================================
  const handleCheckIn = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${process.env.REACT_APP_API_URL}/api/users/checkin`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data.user);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      alert('✓ Check-in successful! Your last activity date has been updated.');
    } catch (err) {
      alert('Failed to check in. Please try again.');
    }
  };

  // ========================================
  // AVATAR MENU TOGGLE
  // ========================================
  const toggleAvatarMenu = () => setAvatarMenuOpen(!avatarMenuOpen);
  const closeAvatarMenu = () => setAvatarMenuOpen(false);

  // ========================================
  // DOWNLOAD DOCUMENT FUNCTION
  // ========================================
  const downloadDocument = async (assetId, fileName) => {
    setDownloadingDoc(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/assets/${assetId}/document`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download document.');
    } finally {
      setDownloadingDoc(false);
    }
  };

  // ========================================
  // DELETE ASSET FUNCTIONS
  // ========================================
  const handleDeleteClick = (asset) => {
    setDeletingAsset(asset);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingAsset) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/assets/${deletingAsset.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssets(assets.filter(a => a.id !== deletingAsset.id));
      setShowDeleteModal(false);
      setDeletingAsset(null);
      fetchUserData();
    } catch (err) {
      alert('Failed to delete asset. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // ========================================
  // REDIRECT TO LOGIN IF NOT AUTHENTICATED
  // ========================================
  useEffect(() => {
    if (user === null) return;
    if (!user) navigate('/login');
  }, [user, navigate]);

  // ========================================
  // FETCH ASSETS
  // ========================================
  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchAssets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/assets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssets(response.data);
    } catch (err) {
      setError('Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // LOGOUT - ONLY IN AVATAR DROPDOWN
  // ========================================
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  // ========================================
  // HELPER FUNCTIONS
  // ========================================
  const getAssetIcon = (type) => {
    const icons = {
      bank_account: '/images/assets/asset-bank.png',
      real_estate: '/images/assets/asset-realestate.png',
      vehicle: '/images/assets/asset-vehicle.png',
      will: '/images/assets/asset-will.png',
      secure_note: '/images/assets/asset-secure-note.png',
      login_credential: '/images/assets/asset-login.png',
      license_code: '/images/assets/asset-license.png',
      custom_asset: '/images/assets/asset-custom.png'
    };
    return icons[type] || '/images/assets/asset-other.png';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GH', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const hasDocument = (asset) => asset.document && asset.document.fileUrl;
  const isTampered = (asset) => asset.integrityWarning || asset.isTampered === true;

  if (!user) return null;

  const limit = user.limits?.totalAssets || 10;
  const usedPercentage = (assets.length / limit) * 100;
  const nokCount = user.globalNextOfKin?.length || 0;

  return (
    <div className="dashboard-container">
      
      {/* ======================================== */}
      {/* HEADER */}
      {/* ======================================== */}
      <header className="dashboard-header">
        
        {/* Desktop Header - All buttons visible */}
        <div className="desktop-header">
          <div className="desktop-header-inner">
            <Link to="/dashboard" className="dashboard-logo">
              <img src="/images/symbols/dashboard-logo-gold.png" alt="Agyapadie Vault" />
            </Link>
            
            <div className="desktop-actions">
              <button onClick={handleCheckIn} className="action-btn checkin">✓ I'm Alive</button>
              <button onClick={() => navigate('/next-of-kin')} className="action-btn">👥 Next of Kin</button>
              <button onClick={() => navigate('/trustees')} className="action-btn">⚖️ Trustees</button>
              <button onClick={() => navigate('/beneficiaries')} className="action-btn">🌍 External</button>
			  <button onClick={() => navigate('/trustee-actions')} className="action-btn">🤝 Trustee Actions</button>  {/* ADD THIS LINE */}
              <button onClick={() => navigate('/upgrade')} className="action-btn upgrade">⭐ Upgrade</button>
              <button onClick={() => navigate('/settings')} className="action-btn settings">⚙️ Settings</button>
              
              {/* Desktop Avatar with Dropdown - Logout ONLY here */}
              <div className="user-profile" onClick={toggleAvatarMenu}>
                <img src="/images/profile/user-avatar.png" alt="" className="user-avatar" />
                <span className="user-name">{user.fullName.split(' ')[0]}</span>
                <span className="avatar-arrow">▼</span>
                {avatarMenuOpen && (
                  <div className="avatar-dropdown">
                    <Link to="/account" onClick={closeAvatarMenu}>
                      👤 My Account
                    </Link>
                    <Link to="/settings" onClick={closeAvatarMenu}>
                      ⚙️ Verification Settings
                    </Link>
                    <button onClick={() => { handleLogout(); closeAvatarMenu(); }}>
                      🚪 Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Mobile Header - Row 1: Logo + Hamburger */}
        <div className="mobile-header">
          <div className="mobile-header-row">
            <Link to="/dashboard" className="dashboard-logo">
              <img src="/images/symbols/dashboard-logo-gold.png" alt="Agyapadie Vault" />
            </Link>
            <button 
              className="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
            >
              {mobileMenuOpen ? '✕' : '☰'}
            </button>
          </div>
          
          {/* Mobile Header - Row 2: I'm Alive + Upgrade + Avatar (Logout ONLY in dropdown) */}
<div className="mobile-critical-buttons">
  <button onClick={handleCheckIn} className="mobile-checkin-btn">✓ I'm Alive</button>
  
  {/* Upgrade Button - Only show if not already on Enterprise plan */}
  {user.plan !== 'enterprise' && (
    <button onClick={() => navigate('/upgrade')} className="mobile-upgrade-btn">
      ⭐ Upgrade
    </button>
  )}

  {/* ADD THIS BUTTON FOR MOBILE */}
  <button onClick={() => navigate('/trustee-actions')} className="mobile-trustee-actions-btn">
    🤝 Trustee Actions
  </button>
  
  <div className="mobile-avatar" onClick={toggleAvatarMenu}>
    <img src="/images/profile/user-avatar.png" alt="" />
    <span>{user.fullName.split(' ')[0]}</span>
    <span className="mobile-arrow">▼</span>
    {avatarMenuOpen && (
      <div className="mobile-avatar-dropdown">
        <Link to="/account" onClick={closeAvatarMenu}>
          👤 My Account
        </Link>
        <Link to="/settings" onClick={closeAvatarMenu}>
          ⚙️ Verification Settings
        </Link>
        <button onClick={() => { handleLogout(); closeAvatarMenu(); }}>
          🚪 Logout
        </button>
      </div>
    )}
  </div>
</div>
        </div>
        
        {/* Mobile Hamburger Menu - Non-critical navigation */}
        <div className={`mobile-nav-menu ${mobileMenuOpen ? 'open' : ''}`}>
          <button onClick={() => { navigate('/next-of-kin'); setMobileMenuOpen(false); }} className="mobile-nav-btn">
            👥 Next of Kin
          </button>
          <button onClick={() => { navigate('/trustees'); setMobileMenuOpen(false); }} className="mobile-nav-btn">
            ⚖️ Trustees
          </button>
          <button onClick={() => { navigate('/beneficiaries'); setMobileMenuOpen(false); }} className="mobile-nav-btn">
            🌍 External
          </button>
          <button onClick={() => { navigate('/upgrade'); setMobileMenuOpen(false); }} className="mobile-nav-btn upgrade">
            ⭐ Upgrade
          </button>
          <button onClick={() => { navigate('/settings'); setMobileMenuOpen(false); }} className="mobile-nav-btn">
            ⚙️ Settings
          </button>
        </div>
        
      </header>

      {/* ======================================== */}
      {/* STATS CARDS - With Quick-Action "+" Buttons */}
      {/* ======================================== */}
      <div className="stats-grid">
        <div className="stat-card" onClick={() => navigate('/add-asset')}>
          <div className="stat-value">{assets.length}</div>
          <div className="stat-label">Total Assets</div>
          <button className="stat-action-btn" aria-label="Add new asset">+</button>
        </div>
        <div className="stat-card" onClick={() => navigate('/next-of-kin')}>
          <div className="stat-value">{nokCount}</div>
          <div className="stat-label">Global NOKs</div>
          <button className="stat-action-btn" aria-label="Add Next of Kin">+</button>
        </div>
        <div className="stat-card" onClick={() => navigate('/trustees')}>
          <div className="stat-value">{trusteesCount}</div>
          <div className="stat-label">Trustees</div>
          <button className="stat-action-btn" aria-label="Add Trustee">+</button>
        </div>
      </div>

      {/* ======================================== */}
      {/* PLAN PROGRESS CARD */}
      {/* ======================================== */}
      <div className="plan-card">
        <div className="plan-header">
          <img src={`/images/plans/plan-badge-${user.plan}.png`} alt={user.plan} className="plan-badge" />
          <div className="plan-info">
            <span className="plan-name">{user.plan.toUpperCase()} PLAN</span>
            <span className="asset-count">{assets.length} of {limit} assets</span>
          </div>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${usedPercentage}%` }}></div>
        </div>
        <button onClick={() => navigate('/upgrade')} className="upgrade-btn">
          {user.plan === 'free' ? 'Upgrade to Premium' : 'Manage Plan'}
        </button>
      </div>

      {/* ======================================== */}
      {/* TAB NAVIGATION */}
      {/* ======================================== */}
      <div className="tab-container">
        <button
          onClick={() => setActiveTab('assets')}
          className={`tab ${activeTab === 'assets' ? 'active' : ''}`}
        >
          📦 My Assets
        </button>
        <button
          onClick={() => setActiveTab('inheritance')}
          className={`tab ${activeTab === 'inheritance' ? 'active' : ''}`}
        >
          📜 Inheritance
        </button>
      </div>

      {/* ======================================== */}
      {/* CONDITIONAL CONTENT */}
      {/* ======================================== */}
      {activeTab === 'assets' ? (
<div className="assets-section">
  {/* Row 1: Title + Add Button */}
  <div className="assets-header-row">
    <h2>Your Assets</h2>
    <button onClick={() => navigate('/add-asset')} className="add-asset-btn">
      + Add New Asset
    </button>
  </div>
  
  {/* Row 2: Search Bar */}
  <div className="assets-search-row">
    <div className="search-container">
      <input
        type="text"
        placeholder="🔍 Search assets..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="search-input"
      />
      {searchTerm && (
        <button onClick={() => setSearchTerm('')} className="search-clear" title="Clear search">
          ✕
        </button>
      )}
    </div>
  </div>
  
  {/* Row 3: Category Filters */}
  <div className="category-filters">
    <button onClick={() => setSelectedCategory('all')} className={`category-btn ${selectedCategory === 'all' ? 'active' : ''}`}>
      All
    </button>
    <button onClick={() => setSelectedCategory('personal')} className={`category-btn ${selectedCategory === 'personal' ? 'active' : ''}`}>
      👤 Personal
    </button>
    <button onClick={() => setSelectedCategory('financial')} className={`category-btn ${selectedCategory === 'financial' ? 'active' : ''}`}>
      💰 Financial
    </button>
    <button onClick={() => setSelectedCategory('property')} className={`category-btn ${selectedCategory === 'property' ? 'active' : ''}`}>
      🏠 Property
    </button>
    <button onClick={() => setSelectedCategory('vehicle')} className={`category-btn ${selectedCategory === 'vehicle' ? 'active' : ''}`}>
      🚗 Vehicle
    </button>
    <button onClick={() => setSelectedCategory('work')} className={`category-btn ${selectedCategory === 'work' ? 'active' : ''}`}>
      💼 Work
    </button>
    <button onClick={() => setSelectedCategory('family')} className={`category-btn ${selectedCategory === 'family' ? 'active' : ''}`}>
      👨‍👩‍👧‍👦 Family
    </button>
    <button onClick={() => setSelectedCategory('legal')} className={`category-btn ${selectedCategory === 'legal' ? 'active' : ''}`}>
      ⚖️ Legal
    </button>
    <button onClick={() => setSelectedCategory('other')} className={`category-btn ${selectedCategory === 'other' ? 'active' : ''}`}>
      📦 Other
    </button>
  </div>
  
  {/* Rest of your assets content (error, loading, empty state, asset grid) */}


          {error && <div className="error-box">{error}</div>}

          {loading ? (
            <div className="loading-state">
              <img src="/images/icons/spinner-gold.gif" alt="Loading" className="spinner" />
              <p>Loading your assets...</p>
            </div>
          ) : filteredAssets.length === 0 ? (
            <div className="empty-state">
			{searchTerm ? (
      <>
        <img src="/images/dashboard/empty-state.png" alt="No results" className="empty-image" />
        <p>No assets match "{searchTerm}"</p>
        <button onClick={() => setSearchTerm('')} className="empty-btn">
          Clear Search
        </button>
      </>
    ) : (
	<>
			
              <img src="/images/dashboard/empty-state.png" alt="No assets" className="empty-image" />
              <p>You haven't added any assets yet.</p>
              <button onClick={() => navigate('/add-asset')} className="empty-btn">
                Add Your First Asset
              </button>
			  </>
			)}
            </div>
          ) : (
            <div className="asset-grid">
              {filteredAssets.map((asset) => (
                <div key={asset.id} className="asset-card">
                  
                  {/* Badges Row */}
                  <div className="badges-row">
                    {asset.certificateNumber && (
                      <span className="badge cert" title={`Certificate: ${asset.certificateNumber}`}>
                        📜 {asset.certificateNumber.slice(-8)}
                      </span>
                    )}
                    {isTampered(asset) && (
                      <span className="badge warning" title="This asset may have been tampered with">
                        ⚠️ Tampered
                      </span>
                    )}
                    {hasDocument(asset) && (
                      <button className="badge doc" onClick={(e) => {
                        e.stopPropagation();
                        downloadDocument(asset.id, asset.document?.fileName || 'document');
                      }} title="Download supporting document">
                        📎 Doc
                      </button>
                    )}
                    <button className="badge delete" onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(asset);
                    }} title="Delete asset (cannot be undone)">
                      🗑️ Delete
                    </button>
                  </div>
                  
                  {/* Asset Content */}
                  <div className="asset-content">
                    <img src={getAssetIcon(asset.assetType)} alt="" className="asset-icon" />
                    <div className="asset-details">
                      <h3 className="asset-name">
                        {asset.assetName || asset.institutionName || 'Unnamed Asset'}
                      </h3>
					  
					  {asset.category && asset.category !== 'other' && (
  <span className={`category-badge category-${asset.category}`}>
    {asset.category === 'personal' && '👤 Personal'}
    {asset.category === 'financial' && '💰 Financial'}
    {asset.category === 'property' && '🏠 Property'}
    {asset.category === 'vehicle' && '🚗 Vehicle'}
    {asset.category === 'work' && '💼 Work'}
    {asset.category === 'family' && '👨‍👩‍👧‍👦 Family'}
    {asset.category === 'legal' && '⚖️ Legal'}
  </span>
)}
					  
                      <p className="asset-type">{asset.assetType?.replace('_', ' ')}</p>
                      {asset.accountNumber && (
                        <p className="asset-detail">Account: {asset.accountNumber}</p>
                      )}
                      {asset.estimatedValue && (
                        <p className="asset-value">{getCurrencySymbol(asset.currency || 'GHS')}{asset.estimatedValue.toLocaleString()}</p>
                      )}
                      <p className="asset-date">Added: {formatDate(asset.createdAt)}</p>
                    </div>
                    <div className="asset-actions">
                      <button onClick={() => navigate(`/asset/${asset.id}`)} className="view-details-btn">
                        View Details
                      </button>
                      <span className="asset-status">{asset.status}</span>
                    </div>
                  </div>
                  
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <InheritanceSection />
      )}

      {/* ======================================== */}
      {/* FOOTER */}
      {/* ======================================== */}
      <Footer />

      {/* ======================================== */}
      {/* DELETE CONFIRMATION MODAL */}
      {/* ======================================== */}
      {showDeleteModal && deletingAsset && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-icon">⚠️</div>
            <h2 className="modal-title">Delete Asset?</h2>
            <p className="modal-text">
              Are you sure you want to delete <strong>"{deletingAsset.assetName || deletingAsset.institutionName || 'this asset'}"</strong>?
            </p>
            <p className="modal-warning">
              This action <strong>cannot be undone</strong>. The asset certificate will become invalid.
              If you made a mistake, you will need to create a new asset from scratch.
            </p>
            <div className="modal-buttons">
              <button onClick={() => setShowDeleteModal(false)} className="modal-cancel" disabled={deleting}>
                Cancel
              </button>
              <button onClick={confirmDelete} className="modal-delete" disabled={deleting}>
                {deleting ? 'Deleting...' : 'Yes, Delete Asset'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================================== */}
      {/* STYLES */}
      {/* ======================================== */}
      <style dangerouslySetInnerHTML={{ __html: `
        
        .dashboard-container {
          min-height: 100vh;
          background: #F5F0E6;
          font-family: 'Montserrat', sans-serif;
          display: flex;
          flex-direction: column;
		  overflow-x: hidden;
		  width: 100%;
        }
        
        /* ======================================== */
        /* DESKTOP HEADER (visible on >768px) */
        /* ======================================== */
        .desktop-header {
          background: #1A1A1A;
          border-bottom: 2px solid #FFD700;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        
        .desktop-header-inner {
          max-width: 1400px;
          margin: 0 auto;
          padding: 15px 30px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .dashboard-logo img {
          height: 45px;
          width: auto;
        }
        
        .desktop-actions {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }
        
        .action-btn {
          padding: 8px 16px;
          border-radius: 5px;
          font-size: 13px;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.3s;
          border: none;
          min-height: 40px;
          white-space: nowrap;
        }
        
        .action-btn.checkin { background: #2E7D32; color: white; }
        .action-btn.upgrade { background: #FFD700; color: #1A1A1A; }
        .action-btn.settings { background: transparent; border: 1px solid #FFD700; color: #FFD700; }
        .action-btn { background: #333; color: #F5F0E6; }
        
        /* Desktop Avatar Dropdown */
        .user-profile {
          position: relative;
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          padding: 4px 12px;
          border-radius: 5px;
          background: rgba(255,255,255,0.05);
        }
        
        .user-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 2px solid #FFD700;
        }
        
        .user-name {
          color: #F5F0E6;
          font-size: 14px;
          font-weight: 500;
        }
        
        .avatar-arrow {
          font-size: 10px;
          color: #FFD700;
        }
        
        .avatar-dropdown {
          position: absolute;
          top: 100%;
          right: 0;
          margin-top: 8px;
          background: #1A1A1A;
          border: 1px solid #FFD700;
          border-radius: 5px;
          min-width: 200px;
          z-index: 100;
          overflow: hidden;
        }
        
        .avatar-dropdown a,
        .avatar-dropdown button {
          display: block;
          width: 100%;
          padding: 12px 16px;
          color: #F5F0E6;
          text-decoration: none;
          background: none;
          border: none;
          text-align: left;
          cursor: pointer;
          font-size: 14px;
          transition: background 0.3s;
        }
        
        .avatar-dropdown a:hover,
        .avatar-dropdown button:hover {
          background: #333;
        }
        
        /* ======================================== */
        /* MOBILE HEADER (visible on <768px) */
        /* ======================================== */
        .mobile-header {
          display: none;
          background: #1A1A1A;
          border-bottom: 2px solid #FFD700;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        
        .mobile-header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 20px;
        }
        
        .mobile-menu-toggle {
          background: none;
          border: none;
          color: #FFD700;
          font-size: 28px;
          cursor: pointer;
          padding: 8px;
          min-width: 44px;
        }
        
        /* Premium Mobile Critical Buttons - border-radius: 5px to match public pages */
        .mobile-critical-buttons {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          gap: 12px;
          padding: 10px 20px;
          background: #1A1A1A;
          border-top: 1px solid #FFD700;
        }
        
        .mobile-checkin-btn {
          padding: 8px 20px;
          background: #2E7D32;
          color: white;
          border: none;
          border-radius: 5px;
          font-size: 13px;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.3s;
        }
        
        .mobile-checkin-btn:hover {
          background: #1B5E20;
        }
        
        /* Mobile Avatar with Dropdown - opens DOWN (top: 100%) */
        .mobile-avatar {
          position: relative;
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          padding: 4px 12px;
          border-radius: 5px;
          background: rgba(255,255,255,0.1);
        }
        
        .mobile-avatar img {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: 2px solid #FFD700;
        }
        
        .mobile-avatar span {
          color: #FFD700;
          font-size: 13px;
          font-weight: 500;
        }
        
        .mobile-arrow {
          font-size: 10px;
        }
        
        .mobile-avatar-dropdown {
          position: absolute;
          top: 100%;
		  left: auto;
          right: 0;
          margin-top: 8px;
          background: #1A1A1A;
          border: 1px solid #FFD700;
          border-radius: 8px;
          min-width: 180px;
          z-index: 100;
        }
        
        .mobile-avatar-dropdown a,
        .mobile-avatar-dropdown button {
          display: block;
          width: 100%;
          padding: 12px 16px;
          color: #F5F0E6;
          text-decoration: none;
          background: none;
          border: none;
          text-align: left;
          cursor: pointer;
          font-size: 14px;
		  box-sizing: border-box;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        
        .mobile-avatar-dropdown a:hover,
        .mobile-avatar-dropdown button:hover {
          background: #333;
        }
        
        /* Mobile Hamburger Menu */
        .mobile-nav-menu {
          display: none;
          flex-direction: column;
          background: #1A1A1A;
          padding: 15px 20px;
          gap: 10px;
          border-top: 1px solid #FFD700;
        }
        
        .mobile-nav-menu.open {
          display: flex;
        }
        
        .mobile-nav-btn {
          padding: 12px;
          background: #333;
          border: none;
          border-radius: 8px;
          color: #F5F0E6;
          font-size: 14px;
          font-weight: bold;
          cursor: pointer;
          text-align: center;
          min-height: 44px;
        }
        
        .mobile-nav-btn.upgrade {
          background: #FFD700;
          color: #1A1A1A;
        }
		
		.mobile-upgrade-btn {
  padding: 8px 20px;
  background: #FFD700;
  color: #1A1A1A;
  border: none;
  border-radius: 5px;
  font-size: 13px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s;
}

.mobile-upgrade-btn:hover {
  background: #B8860B;
}
        
        /* ======================================== */
        /* STATS GRID */
        /* ======================================== */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          margin: 30px 30px 30px 30px;
        }
        
        .stat-card {
          background: white;
          border-radius: 12px;
          padding: 25px;
          text-align: center;
          box-shadow: 0 4px 15px rgba(0,0,0,0.05);
          transition: all 0.3s;
          cursor: pointer;
          position: relative;
        }
        
        .stat-card:hover { transform: translateY(-3px); }
        .stat-value { font-size: 36px; font-weight: bold; color: #FFD700; margin-bottom: 8px; }
        .stat-label { font-size: 14px; color: #666; margin-bottom: 10px; }
        
        .stat-action-btn {
          background: #FFD700;
          border: none;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          font-size: 18px;
          font-weight: bold;
          cursor: pointer;
          position: absolute;
          bottom: 15px;
          right: 15px;
        }
        
        /* ======================================== */
        /* PLAN CARD */
        /* ======================================== */
        .plan-card {
          margin: 0 30px 30px 30px;
          padding: 20px 25px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 15px rgba(0,0,0,0.05);
        }
        
        .plan-header { display: flex; align-items: center; gap: 15px; margin-bottom: 15px; }
        .plan-badge { width: 50px; height: auto; }
        .plan-info { flex: 1; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; }
        .plan-name { font-size: 18px; font-weight: bold; color: #1A1A1A; }
        .asset-count { font-size: 14px; color: #666; }
        .progress-bar { height: 8px; background: #E5E7EB; border-radius: 4px; overflow: hidden; margin-bottom: 15px; }
        .progress-fill { height: 100%; background: #FFD700; transition: width 0.3s ease; }
        .upgrade-btn { width: 100%; padding: 12px; background: #FFD700; border: none; border-radius: 8px; color: #1A1A1A; font-size: 14px; font-weight: bold; cursor: pointer; min-height: 44px; }
        
        /* ======================================== */
        /* TABS */
        /* ======================================== */
        .tab-container {
          display: flex;
          gap: 10px;
          margin: 0 30px 20px 30px;
          border-bottom: 1px solid #ddd;
          padding-bottom: 10px;
        }
        
        .tab {
          padding: 12px 24px;
          background: transparent;
          border: none;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          border-radius: 8px;
          min-height: 44px;
        }
        
        .tab.active { background: #FFD700; color: #1A1A1A; }
        
        /* ======================================== */
        /* ASSETS SECTION */
        /* ======================================== */
        .assets-section {
          margin: 0 30px 30px 30px;
          padding: 25px;
          background: white;
          border-radius: 15px;
          box-shadow: 0 4px 15px rgba(0,0,0,0.05);
        }
        
        .assets-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 25px; flex-wrap: wrap; gap: 15px; }
        .assets-header h2 { font-size: 20px; color: #1A1A1A; margin: 0; }
        .add-asset-btn { padding: 10px 20px; background: #FFD700; border: none; border-radius: 8px; color: #1A1A1A; font-size: 14px; font-weight: bold; cursor: pointer; min-height: 44px; }
        
        .asset-grid {
			display: grid;
			grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
			gap: 20px; 
			width: 100%;
			overflow-x: hidden;
			}
			
        .asset-card { background: #F5F0E6; border-radius: 12px; padding: 16px; transition: all 0.3s; }
        .asset-card:hover { transform: translateY(-3px); box-shadow: 0 8px 25px rgba(0,0,0,0.1); }
        
        .badges-row { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 15px; }
        .badge { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 500; }
        .badge.cert { background: #FFD700; color: #1A1A1A; font-family: monospace; }
        .badge.warning { background: #fee2e2; border: 1px solid #ef4444; color: #dc2626; }
        .badge.doc { background: #2E7D32; color: white; cursor: pointer; }
        .badge.delete { background: #dc2626; color: white; cursor: pointer; }
        
        .asset-content { display: flex; gap: 15px; align-items: flex-start; }
        .asset-icon { width: 60px; height: 60px; flex-shrink: 0; }
        .asset-details { flex: 1; }
        .asset-name { font-size: 16px; font-weight: bold; color: #1A1A1A; margin-bottom: 5px; }
        .asset-type { font-size: 12px; color: #666; text-transform: capitalize; margin-bottom: 5px; }
        .asset-detail { font-size: 13px; color: #1A1A1A; margin-bottom: 3px; }
        .asset-value { font-size: 14px; font-weight: bold; color: #2E7D32; margin-bottom: 5px; }
        .asset-date { font-size: 11px; color: #999; }
        .asset-actions { display: flex; flex-direction: column; align-items: flex-end; gap: 8px; flex-shrink: 0; }
        .view-details-btn { padding: 8px 16px; background: transparent; border: 1px solid #2E7D32; color: #2E7D32; border-radius: 6px; font-size: 12px; cursor: pointer; white-space: nowrap; min-height: 36px; }
        .asset-status { font-size: 11px; color: #666; text-transform: capitalize; }
        
        /* Error & Empty */
        .error-box { background: #fee2e2; border: 1px solid #ef4444; color: #dc2626; padding: 12px; border-radius: 8px; margin-bottom: 20px; }
        .loading-state { text-align: center; padding: 60px; }
        .spinner { width: 50px; height: 50px; margin-bottom: 10px; }
        .empty-state { text-align: center; padding: 60px 20px; }
        .empty-image { width: 180px; height: auto; margin-bottom: 20px; opacity: 0.6; }
        .empty-btn { padding: 12px 30px; background: #FFD700; border: none; border-radius: 8px; color: #1A1A1A; font-size: 16px; font-weight: bold; cursor: pointer; margin-top: 20px; min-height: 48px; }
        
        /* Modal */
        .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .modal-content { background: white; border-radius: 16px; padding: 30px; max-width: 450px; width: 90%; text-align: center; }
        .modal-icon { font-size: 48px; margin-bottom: 15px; }
        .modal-title { font-size: 24px; color: #dc2626; margin-bottom: 15px; }
        .modal-text { font-size: 16px; color: #333; margin-bottom: 15px; }
        .modal-warning { font-size: 14px; color: #666; background: #F5F0E6; padding: 12px; border-radius: 8px; margin-bottom: 25px; }
        .modal-buttons { display: flex; gap: 15px; justify-content: center; }
        .modal-cancel, .modal-delete { padding: 12px 25px; border-radius: 8px; font-size: 14px; font-weight: bold; cursor: pointer; min-height: 44px; }
        .modal-cancel { background: transparent; border: 1px solid #ccc; }
        .modal-delete { background: #dc2626; border: none; color: white; }
        
        /* ======================================== */
        /* RESPONSIVE BREAKPOINTS */
        /* ======================================== */
        
        @media (max-width: 1024px) {
          .stats-grid { gap: 15px; }
          .asset-grid { grid-template-columns: repeat(2, 1fr); }
        }
        
        @media (max-width: 768px) {
          .desktop-header { display: none; }
          .mobile-header { display: block; }
          
          .stats-grid { grid-template-columns: 1fr; margin: 20px 20px 20px 20px; gap: 12px; }
          .plan-card { margin: 0 20px 20px 20px; }
          .tab-container { margin: 0 20px 15px 20px; }
          .assets-section { margin: 0 20px 20px 20px; padding: 20px; }
          .asset-grid { grid-template-columns: 1fr; }
          
          .asset-content { gap: 12px; }
          .asset-icon { width: 50px; height: 50px; }
        }
        
		@media (max-width: 480px) {
  .stat-value { font-size: 28px; }
  .asset-content { flex-direction: column; align-items: center; text-align: center; gap: 10px; }
  .badges-row { justify-content: center; }
  .asset-actions { flex-direction: row; justify-content: center; width: 100%; }
  .mobile-checkin-btn,
  .mobile-upgrade-btn {
    padding: 6px 14px;
    font-size: 12px;
  }
  .mobile-avatar span {
    display: none;
  }
  .mobile-avatar img {
    width: 32px;
    height: 32px;
  }
  .mobile-avatar-dropdown {
    min-width: 140px;
  }
  .mobile-avatar-dropdown a,
  .mobile-avatar-dropdown button {
    white-space: nowrap;
    padding: 6px 8px;
    font-size: 12px;
  }
}
        
      `}} />
      
    </div>
  );
};

export default Dashboard;